import { RfqAccept, RfqNetwork, RfqState, RfqUpdate } from '@/types/generated/rfq'

const TERMINAL_STATES: ReadonlySet<RfqState> = new Set([
  RfqState.RFQ_STATE_FILLED,
  RfqState.RFQ_STATE_CANCELLED,
  RfqState.RFQ_STATE_EXPIRED,
])

export function isTerminalRfqState(state: RfqState): boolean {
  return TERMINAL_STATES.has(state)
}

/**
 * `RFQ_NETWORK_UNSPECIFIED` (0) is Deribit-native, NOT "unknown". Absent proto fields
 * decode to 0, so every RFQ frame minted before the network discriminator existed — i.e.
 * every frame in dev/prod today — reads as 0 and must render as Deribit. `UNRECOGNIZED`
 * folds the same way for the same reason: within Deribit-settled flow there is no third
 * network, so anything not explicitly Paradigm is the Deribit leg.
 *
 * Only meaningful once the account's `tradeVenue` is known to be Deribit — on a venue
 * with its own unrelated RFQ implementation (Derive) 0 means "not applicable".
 */
export function normalizeRfqNetwork(network: RfqNetwork): RfqNetwork {
  return network === RfqNetwork.RFQ_NETWORK_PARADIGM
    ? RfqNetwork.RFQ_NETWORK_PARADIGM
    : RfqNetwork.RFQ_NETWORK_DERIBIT
}

/**
 * The store key for anything per-network. One OrderX `rfqId` can have a live row on
 * Deribit AND on Paradigm at once — two independent quote books racing to fill — so
 * keying by `rfqId` alone lets whichever frame lands last silently overwrite the other
 * leg's legs/quotes/state.
 */
export function rfqRowKey(row: { rfqId: string; rfqNetwork: RfqNetwork }): string {
  return `${row.rfqId} ${normalizeRfqNetwork(row.rfqNetwork)}`
}

/** Numeric (BigInt) compare of the int64 `updatedAtMs` strings; bad input sorts as 0. */
function updatedAt(rfq: RfqUpdate): bigint {
  try {
    return BigInt(rfq.updatedAtMs || '0')
  } catch {
    return 0n
  }
}

/** Per-account cap on live RFQ rows retained in `rfqsRawAtom` — see `capRfqsPerAccount`. */
export const MAX_RFQS_PER_ACCOUNT = 500

/**
 * Enrich the winning FILLED snapshot with a price from the other FILLED snapshot.
 * Paradigm's RFQ channel and fill channel may arrive in either order. Never change
 * the winner's other fields or overwrite a price it already has.
 */
function keepFillPrice(frame: RfqUpdate, existing: RfqUpdate): RfqUpdate {
  if (frame.state !== RfqState.RFQ_STATE_FILLED || existing.state !== RfqState.RFQ_STATE_FILLED) {
    return frame
  }
  if (frame.filledPrice || !existing.filledPrice) return frame
  return { ...frame, filledPrice: existing.filledPrice }
}

/**
 * Merge live `RfqUpdate` frames into the current list. Each frame is a complete snapshot
 * of one RFQ **on one network**, so we replace legs/quotes wholesale (never diff-merge)
 * and key by `(rfqId, rfqNetwork)`. A frame whose `updatedAtMs` is older than what we
 * already hold for that same key cannot replace it, but can supply a missing fill price.
 * `updatedAtMs` is the venue's monotonic
 * logical stamp, so this collapses reconnect/re-emit duplicates and out-of-order frames.
 *
 * That de-dupe is scoped to one network's own frame sequence on purpose. Comparing
 * `updatedAtMs` ACROSS networks is unsafe: Deribit stamps a terminal frame with the RFQ's
 * `expiresAtMs` (fixed at creation, always >= every real event before expiry) while
 * Paradigm stamps a genuine event time — so a Deribit CANCELLED would deterministically
 * outrank a real Paradigm FILLED and make the user's fill vanish. Cross-network state
 * resolution happens in `foldRfqNetworkStates`, never here.
 *
 * Existing entries keep their position; genuinely new rows are appended — then the result
 * is capped per account (see `capRfqsPerAccount`).
 */
export function upsertRfqs(current: RfqUpdate[], incoming: RfqUpdate[]): RfqUpdate[] {
  const result = current.slice()
  const indexByKey = new Map<string, number>()
  result.forEach((rfq, i) => indexByKey.set(rfqRowKey(rfq), i))

  for (const frame of incoming) {
    const key = rfqRowKey(frame)
    const existingIndex = indexByKey.get(key)
    if (existingIndex === undefined) {
      indexByKey.set(key, result.length)
      result.push(frame)
      continue
    }
    const existing = result[existingIndex]
    const existingTerminal = isTerminalRfqState(existing.state)
    const frameTerminal = isTerminalRfqState(frame.state)
    if (existingTerminal && !frameTerminal) continue
    if (
      (!existingTerminal && frameTerminal) ||
      (existingTerminal === frameTerminal && updatedAt(frame) >= updatedAt(existing))
    ) {
      result[existingIndex] = keepFillPrice(frame, existing)
    } else {
      // A late priced fill can enrich the newer snapshot without rolling back its metadata.
      result[existingIndex] = keepFillPrice(existing, frame)
    }
  }

  return capRfqsPerAccount(result)
}

/** Mark an accepted cancel immediately; a later terminal venue frame can still replace it. */
export function markRfqCancelled(
  current: RfqUpdate[],
  identity: Pick<RfqUpdate, 'accountId' | 'rfqId'>
): RfqUpdate[] {
  let changed = false
  const next = current.map((rfq) => {
    if (rfq.accountId !== identity.accountId || rfq.rfqId !== identity.rfqId) return rfq
    if (isTerminalRfqState(rfq.state)) return rfq
    changed = true
    return {
      ...rfq,
      state: RfqState.RFQ_STATE_CANCELLED,
    }
  })
  return changed ? next : current
}

/**
 * Bounds the live RFQ stream by capping each account's own contribution, same reasoning
 * and grouping as `capHistoryPerAccount` (account-order-merge.ts) uses for order/trade
 * history — `upsertRfqs` never drops a row on its own (only replaces or appends), so
 * without this `rfqsRawAtom` would grow for the entire browser session on an active RFQ
 * desk. `mergeRfqViews` re-merges against the separately-paginated REST history, so this
 * only bounds the in-memory live-stream tail, not what a user can see.
 *
 * Eviction works on whole `rfqId` groups, never on individual network rows: dropping one
 * leg of a dual-network RFQ would leave the survivor to fold on its own, which is exactly
 * how a real Paradigm fill disappears behind Deribit's expiry-stamped CANCELLED row. A
 * group is "active" (never trimmed) if ANY of its network rows is non-terminal — there
 * are only ever a handful open on an account at once, so they can't be the source of
 * unbounded growth. Cross-network `updatedAtMs` is compared only to order which stale
 * terminal group to discard first, never to decide a displayed state.
 */
export function capRfqsPerAccount(
  items: RfqUpdate[],
  maxPerAccount: number = MAX_RFQS_PER_ACCOUNT
): RfqUpdate[] {
  const byAccount = new Map<string, RfqUpdate[]>()
  for (const item of items) {
    const list = byAccount.get(item.accountId)
    if (list) list.push(item)
    else byAccount.set(item.accountId, [item])
  }

  const result: RfqUpdate[] = []
  for (const list of byAccount.values()) {
    if (list.length <= maxPerAccount) {
      result.push(...list)
      continue
    }

    const groups = new Map<string, RfqUpdate[]>()
    for (const item of list) {
      const group = groups.get(item.rfqId)
      if (group) group.push(item)
      else groups.set(item.rfqId, [item])
    }

    const active: RfqUpdate[] = []
    const terminal: { rows: RfqUpdate[]; newest: bigint }[] = []
    for (const rows of groups.values()) {
      if (rows.some((rfq) => !isTerminalRfqState(rfq.state))) {
        active.push(...rows)
        continue
      }
      const newest = rows.reduce((max, r) => (updatedAt(r) > max ? updatedAt(r) : max), 0n)
      terminal.push({ rows, newest })
    }
    terminal.sort((a, b) => (b.newest > a.newest ? 1 : b.newest < a.newest ? -1 : 0))

    result.push(...active)
    let budget = Math.max(0, maxPerAccount - active.length)
    for (const group of terminal) {
      if (group.rows.length > budget) break
      result.push(...group.rows)
      budget -= group.rows.length
    }
  }
  return result
}

/**
 * Collapse a `get_rfq_history` response to one row per `(rfqId, rfqNetwork)`.
 *
 * `GetRfqHistoryResponse.rfqs` is the full lifecycle TIMELINE, newest-first — many rows
 * per network, not one. The backend's reconcile pass rewrites ONLY the newest row per
 * `(rfqId, rfqNetwork)` to the cross-network folded outcome; every older row keeps its
 * raw historical state. So the first row per key is the reconciled, trustworthy one, and
 * the rows underneath it are NOT a state history that can be rendered.
 *
 * Concretely, a dual-network RFQ that filled on Deribit comes back with a PARADIGM row
 * rewritten to FILLED sitting directly on top of the PARADIGM row that still says
 * CANCELLED (what actually happened to the losing leg, 27ms earlier). Render those as a
 * timeline and the user sees "CANCELLED then FILLED" on one leg and reasonably concludes
 * something broke. Take the first row per key; drop the rest.
 */
export function latestRfqRowPerNetwork(rows: RfqUpdate[]): RfqUpdate[] {
  const seen = new Set<string>()
  const result: RfqUpdate[] = []
  for (const row of rows) {
    const key = rfqRowKey(row)
    if (seen.has(key)) continue
    seen.add(key)
    result.push(row)
  }
  return result
}

/**
 * Merge the history-seed rows with the live stream snapshots for display, keyed by
 * `(rfqId, rfqNetwork)` — one network's frames must never overwrite the other's. Pass
 * `history` through `latestRfqRowPerNetwork` first.
 *
 * Within a single network key, a terminal state (FILLED/CANCELLED/EXPIRED) from EITHER
 * source always wins over a non-terminal one — a stale lingering OPEN stream frame must
 * never resurrect a filled RFQ back into the active list (even if that OPEN frame carries
 * a higher `updatedAtMs`). When both sides agree on terminal-ness, the newer
 * `updatedAtMs` wins, so the live frame (which carries quotes) is preferred for live RFQs.
 */
export function mergeRfqViews(history: RfqUpdate[], live: RfqUpdate[]): RfqUpdate[] {
  const byKey = new Map<string, RfqUpdate>()
  for (const rfq of history) byKey.set(rfqRowKey(rfq), rfq)
  for (const frame of live) {
    const key = rfqRowKey(frame)
    const existing = byKey.get(key)
    if (!existing) {
      byKey.set(key, frame)
      continue
    }
    const existingTerminal = isTerminalRfqState(existing.state)
    const frameTerminal = isTerminalRfqState(frame.state)
    if (existingTerminal && !frameTerminal) continue
    if ((!existingTerminal && frameTerminal) || updatedAt(frame) >= updatedAt(existing)) {
      byKey.set(key, keepFillPrice(frame, existing))
    } else {
      byKey.set(key, keepFillPrice(existing, frame))
    }
  }
  return [...byKey.values()]
}

/**
 * Fold completed auto-RFQ algos into the displayed RFQ list so they still appear in
 * History when the venue never sent a terminal `RfqUpdate` (or sent none at all). The
 * caller synthesizes one terminal-state `RfqUpdate` per terminal `ALGO_TYPE_RFQ` algo
 * (keyed by `RfqAlgoState.currentRfqId`). A terminal algo beats a stale non-terminal
 * venue row; otherwise real venue data wins. The lookup is by `rfqId` alone, so it
 * matches a row **on any network** — a synthesized row has no network of its own to
 * attribute. Rows with no id are skipped. The combined list is fed to `partitionRfqs`,
 * which routes the terminal rows to History.
 */
export function mergeRfqAlgoHistory(rfqs: RfqUpdate[], algoRfqs: RfqUpdate[]): RfqUpdate[] {
  let changed = false
  const result = rfqs.slice()
  const indexesById = new Map<string, number[]>()
  result.forEach((rfq, index) => {
    const indexes = indexesById.get(rfq.rfqId) ?? []
    indexes.push(index)
    indexesById.set(rfq.rfqId, indexes)
  })
  for (const algoRfq of algoRfqs) {
    if (!algoRfq.rfqId) continue
    const indexes = indexesById.get(algoRfq.rfqId)
    if (!indexes) {
      indexesById.set(algoRfq.rfqId, [result.length])
      result.push(algoRfq)
      changed = true
      continue
    }
    for (const index of indexes) {
      if (!isTerminalRfqState(result[index].state) && isTerminalRfqState(algoRfq.state)) {
        // Stop every stale network leg; keep each leg's network identity and any real fills.
        result[index] = {
          ...result[index],
          state: algoRfq.state,
          updatedAtMs: algoRfq.updatedAtMs,
        }
        changed = true
      }
    }
  }
  return changed ? result : rfqs
}

/** CANCELLED outranks EXPIRED — an explicit cancel is more informative than a passive
 * timeout. Mirrors the backend's own `terminal_severity` tie-break. */
function terminalSeverity(state: RfqState): number {
  switch (state) {
    case RfqState.RFQ_STATE_CANCELLED:
      return 2
    case RfqState.RFQ_STATE_EXPIRED:
      return 1
    default:
      return 0
  }
}

/**
 * The cross-network state fold — the ONLY correct way to derive "what happened to this
 * RFQ" once one `rfqId` can live on two networks. Mirrors the backend's
 * `derive_outcome_across_networks`:
 *
 * - any network FILLED wins outright, unconditionally — even against a terminal row on
 *   the other network carrying a numerically larger `updatedAtMs`;
 * - else, if every observed network is terminal, the more severe one shows
 *   (CANCELLED over EXPIRED);
 * - else OPEN.
 *
 * Deliberately takes no timestamps. Resolving this by "latest `updatedAtMs` wins" is the
 * shape of the bug: Deribit stamps terminal frames with `expiresAtMs`, so its CANCELLED
 * (the losing leg of a dual fill being torn down) outranks the real Paradigm FILLED every
 * time, regardless of arrival order, and the user's fill disappears from the UI.
 */
export function foldRfqNetworkStates(states: readonly RfqState[]): RfqState {
  if (states.length === 0) return RfqState.RFQ_STATE_UNSPECIFIED
  if (states.includes(RfqState.RFQ_STATE_FILLED)) return RfqState.RFQ_STATE_FILLED
  if (!states.every(isTerminalRfqState)) return RfqState.RFQ_STATE_OPEN
  return states.reduce((worst, s) => (terminalSeverity(s) > terminalSeverity(worst) ? s : worst))
}

/** One network's leg of an RFQ. */
export interface RfqNetworkRow {
  network: RfqNetwork
  rfq: RfqUpdate
}

/**
 * One OrderX RFQ as the UI shows it: a single card holding up to two per-network legs,
 * plus the cross-network folded state. Manual RFQs — and any auto RFQ that Paradigm
 * declined to quote, since structures below its minimum block size silently degrade to
 * Deribit-only — simply have one row. That is a normal card, not a degraded one.
 */
export interface RfqCard {
  rfqId: string
  /** Deribit first, then Paradigm — enum order, so rendering and tie-breaks are stable. */
  rows: RfqNetworkRow[]
  /** Folded across `rows` via `foldRfqNetworkStates`, never an `updatedAtMs` comparison. */
  state: RfqState
  /** The row whose legs/quotes/timestamps represent the card as a whole. */
  primary: RfqUpdate
}

function isPastExpiry(rfq: RfqUpdate, nowMs: number): boolean {
  const deadline = Number(rfq.expiresAtMs)
  return Number.isFinite(deadline) && deadline > 0 && deadline <= nowMs
}

/**
 * Client-side expiry fallback. The venue's EXPIRED frame is authoritative, but if it's
 * missed (dropped WS frame, reconnect gap) a leg would sit non-terminal forever with a
 * dead countdown. A non-terminal row past its `expiresAtMs` is shown as EXPIRED, stamped
 * with the expiry boundary exactly like a real terminal frame. Display-only — the raw
 * atom keeps the venue frame untouched, so a late real terminal frame still wins.
 */
function withExpiryFallback(rfq: RfqUpdate, nowMs: number | undefined): RfqUpdate {
  if (nowMs === undefined || isTerminalRfqState(rfq.state) || !isPastExpiry(rfq, nowMs)) return rfq
  return { ...rfq, state: RfqState.RFQ_STATE_EXPIRED, updatedAtMs: rfq.expiresAtMs }
}

const NETWORK_ORDER: readonly RfqNetwork[] = [
  RfqNetwork.RFQ_NETWORK_DERIBIT,
  RfqNetwork.RFQ_NETWORK_PARADIGM,
]

/**
 * Group per-network rows into one card per `rfqId` and fold their states. This is the
 * render-time fold the contract asks for — the per-network rows stay intact underneath,
 * so nothing is collapsed into a single stored `updatedAtMs`-ordered value.
 */
export function buildRfqCards(rows: RfqUpdate[], nowMs?: number): RfqCard[] {
  const byRfqId = new Map<string, Map<RfqNetwork, RfqUpdate>>()
  const order: string[] = []
  for (const raw of latestRfqRowPerNetwork(rows)) {
    const rfq = withExpiryFallback(raw, nowMs)
    let networks = byRfqId.get(rfq.rfqId)
    if (!networks) {
      networks = new Map()
      byRfqId.set(rfq.rfqId, networks)
      order.push(rfq.rfqId)
    }
    networks.set(normalizeRfqNetwork(rfq.rfqNetwork), rfq)
  }

  return order.map((rfqId) => {
    const networks = byRfqId.get(rfqId)!
    const cardRows: RfqNetworkRow[] = NETWORK_ORDER.filter((n) => networks.has(n)).map(
      (network) => ({ network, rfq: networks.get(network)! })
    )
    const state = foldRfqNetworkStates(cardRows.map((r) => r.rfq.state))
    // The filled leg is what actually executed, so its quotes/price/timestamps are the
    // ones worth showing; otherwise prefer a leg agreeing with the folded state, and fall
    // back to Deribit-first order.
    const primary =
      cardRows.find((r) => r.rfq.state === RfqState.RFQ_STATE_FILLED) ??
      cardRows.find((r) => r.rfq.state === state) ??
      cardRows[0]
    return { rfqId, rows: cardRows, state, primary: primary.rfq }
  })
}

/**
 * The card flattened back to a single `RfqUpdate`-shaped row: the representative leg,
 * carrying the CROSS-NETWORK FOLDED state rather than that leg's own raw one. Drop-in for
 * every consumer that renders one row per RFQ (the dashboard active/history tables, the
 * RFQs tab) — for a single-network RFQ it is the untouched venue frame, and for a dual
 * one it is the filled leg's legs/quotes/timestamps under the folded outcome.
 *
 * This is the ONLY sanctioned way to get a display state out of an `RfqUpdate` that did
 * not come pre-reconciled from `get_rfq_history`: a raw live frame's `state` is
 * per-network and can read OPEN or CANCELLED for an RFQ that has already filled on the
 * other network (Deribit stamps its terminal lifecycle row only at RFQ expiry, minutes
 * after the fact).
 */
export function cardRow(card: RfqCard): RfqUpdate {
  return card.primary.state === card.state ? card.primary : { ...card.primary, state: card.state }
}

/** Live (non-terminal) cards vs. terminal ones that belong in History — split on the
 * FOLDED state, so a dual-network RFQ can never appear in both lists at once. */
export function partitionRfqCards(cards: RfqCard[]): { active: RfqCard[]; history: RfqCard[] } {
  const active: RfqCard[] = []
  const history: RfqCard[] = []
  for (const card of cards) {
    if (isTerminalRfqState(card.state)) history.push(card)
    else active.push(card)
  }
  return { active, history }
}

/** Cap on `rfqAcceptsAtom` — see `capRfqAccepts`. */
export const MAX_RFQ_ACCEPTS = 500

/**
 * Bounds `rfqAcceptsAtom`, which (unlike `rfqsRawAtom`) is a plain `Record<key,
 * RfqAccept>` that only ever gains keys — nothing in the write path
 * (`account-data-manager.tsx`'s `handleRfqAccept`) ever removes one, so on an active
 * RFQ desk it would otherwise grow for the entire browser session. String object keys
 * iterate in insertion order, so dropping the first `excess` keys evicts the oldest
 * accepts once the map grows past the cap — an approximation of LRU that's good enough
 * here since this is purely a "show the confirmed price" cache, not a source of truth.
 */
export function capRfqAccepts(
  accepts: Record<string, RfqAccept>,
  maxEntries: number = MAX_RFQ_ACCEPTS
): Record<string, RfqAccept> {
  const keys = Object.keys(accepts)
  if (keys.length <= maxEntries) return accepts
  const next = { ...accepts }
  for (const key of keys.slice(0, keys.length - maxEntries)) {
    delete next[key]
  }
  return next
}
